import { Box } from '@chakra-ui/react';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { RThirdTitle } from '@resistapp/client/components/headers/r-third-title';
import { Grid, GridCell } from '@resistapp/client/components/shared/grid';
import { GridView } from '@resistapp/client/components/shared/layout';
import { theme } from '@resistapp/client/components/shared/theme';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useNormalisationMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { useResearchAdmin } from '@resistapp/client/hooks/use-research-admin';
import { compileCtsFiles } from '@resistapp/common/compile-cts-files';
import { getHeatmapCsvExportContent, getNumberOfDetectedGenesByBioNumCsv } from '@resistapp/common/export';
import { hasOverview, isNonDemo } from '@resistapp/common/features';
import { NormalisedValueName } from '@resistapp/common/normalisation-mode';
import { FullProject, Project } from '@resistapp/common/types';
import { chunk, keys, uniq } from 'lodash';
import { useMemo } from 'react';
import { ExternalLinkIcon } from '../../components/icons/external-link-icon';
import { DownloadLink } from '../../components/links/download-link';
import { UrlLink } from '../../components/links/url-link';

interface Props {
  project: FullProject;
}

export function DownloadAndLinkSection({ project }: Props) {
  const { user } = useUser();
  const { isModifyAsAdminActive } = useResearchAdmin();

  const normalisationMode = useNormalisationMode();
  const { getAssayInfo, getGroup } = useAssayContext();
  const plotFileNames = getExtraFilenames(project);
  const qpcrFiles = useMemo(() => compileCtsFiles(project.chips, project.samplesByUID), [project]);
  const hasCtFiles = keys(qpcrFiles).length > 0;

  return (
    <GridView>
      <RSecondTitle>Downloads and links</RSecondTitle>
      <Grid>
        <GridCell widthpercentage={100}>
          <RThirdTitle>Analysis results</RThirdTitle>
          <DownloadLink
            filename={`${NormalisedValueName[normalisationMode]}.csv`}
            getFileContent={() => getHeatmapCsvExportContent(project.samplesByUID, normalisationMode, getAssayInfo)}
          />
          <DownloadLink
            filename="Numbers of detected genes.csv"
            getFileContent={() => getNumberOfDetectedGenesByBioNumCsv(project.samplesByUID, getGroup)}
          />
          {plotFileNames.map((filename, key) => (
            <DownloadLink key={`dplot-${key}`} filename={filename} projectId={project.id} />
          ))}
        </GridCell>
        {user && hasCtFiles && (
          <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
            <RThirdTitle>qPCR CT values</RThirdTitle>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {chunk(keys(qpcrFiles), 4).map((fileChunk, chunkKey) => (
                <div key={`txtchunk${chunkKey}`} style={{ flexDirection: 'column' }}>
                  {fileChunk.map((filename, key) => {
                    const chipId = project.chips.find(chip => `${chip.name}.txt` === filename)?.id;
                    return (
                      <Box key={key} display="flex" alignItems="center">
                        <DownloadLink filename={filename} getFileContent={() => qpcrFiles[filename]} />
                        {isModifyAsAdminActive && chipId && (
                          <span onClick={() => window.open(`/chip/${chipId}`, '_blank')}>
                            <ExternalLinkIcon style={{ cursor: 'pointer' }} />
                          </span>
                        )}
                      </Box>
                    );
                  })}
                </div>
              ))}
            </div>
          </GridCell>
        )}

        {isModifyAsAdminActive ? (
          <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
            <RThirdTitle>Internal</RThirdTitle>
            {project.customerSheetLink && <UrlLink text="Samples and primers sheet" href={project.customerSheetLink} />}
            {uniq(project.chips.map(chip => chip.labSheetUrl)).map(
              (labSheetUrl, index) =>
                labSheetUrl && <UrlLink key={`lab-sheet-${index}`} text={`Lab sheet`} href={labSheetUrl} />,
            )}
          </GridCell>
        ) : (
          isNonDemo(user) &&
          !hasOverview(user) &&
          project.customerSheetLink && (
            <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
              <RThirdTitle>Project specification</RThirdTitle>
              <UrlLink text="Samples and primers sheet" href={project.customerSheetLink} />
            </GridCell>
          )
        )}
      </Grid>
    </GridView>
  );
}

export function getExtraFilenames(project: Project) {
  return project.filenames?.filter(name => name.endsWith('.pdf') || name.endsWith('.csv')) || [];
}
